import {
  api,
  type ApiError,
  type AuthUser,
  type BatchInviteEntry,
  type BatchInviteResult,
  type Organization,
} from '../api/client.js';
import { copyTextWithFeedback } from '../util/copy-feedback.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ORG_DATALIST_ID = 'invite-orgs-datalist';

interface ParsedRow {
  email: string;
  valid: boolean;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}

/**
 * Parses a free-form email string. The textarea accepts emails
 * separated by commas, whitespace, or newlines (any combo). The
 * result preserves first-seen order, lowercases each address, and
 * dedupes case-insensitively. Each row carries a `valid` flag rather
 * than being filtered out so the user can see which inputs failed
 * the basic email regex right next to the offending value.
 */
function parseEmails(raw: string): ParsedRow[] {
  const seen = new Set<string>();
  const out: ParsedRow[] = [];
  for (const piece of raw.split(/[\s,;]+/)) {
    const trimmed = piece.trim().toLowerCase();
    if (!trimmed) continue;
    if (seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push({ email: trimmed, valid: EMAIL_RE.test(trimmed) });
  }
  return out;
}

function statusVariant(s: BatchInviteResult['status']): string {
  switch (s) {
    case 'created':
      return 'success';
    case 'conflict':
      return 'warning';
    case 'invalid':
    case 'org-not-found':
    case 'error':
      return 'danger';
    default:
      return 'neutral';
  }
}

function resultsToTsv(results: BatchInviteResult[]): string {
  const header = ['email', 'organization', 'status', 'tempPassword', 'error'].join(
    '\t',
  );
  const rows = results.map((r) =>
    [
      r.email,
      r.organizationName ?? '',
      r.status,
      r.tempPassword ?? '',
      r.error ?? '',
    ].join('\t'),
  );
  return [header, ...rows].join('\n');
}

/**
 * Opens the "Invite users (batch)" modal. The flow:
 *
 *   1. Compose — paste emails into a textarea (comma / newline /
 *      whitespace separated). A live table renders one row per
 *      parsed email. For superadmins, each row carries an
 *      organization input (free-form; backend
 *      `getOrCreateOrganization` reuses an exact name match or
 *      creates a new one) plus a "default org" input that's
 *      copied into any row left empty at submit time. For org
 *      admins the org is pinned to their own organization and
 *      the per-row / default org inputs are hidden — they cannot
 *      invite into other tenants.
 *   2. Submit — POSTs `/api/users/batch`. The server generates a
 *      strong random temp password per accepted entry; the response
 *      contains the plaintext exactly once.
 *   3. Results — a status badge + temp password (with copy icon)
 *      per row, plus a "Copy all (TSV)" button. Done closes.
 *
 * The first user to land in any organization is auto-promoted to
 * `org_admin` server-side, regardless of who invited them.
 *
 * Resolves with the per-entry results (or `null` if dismissed before
 * submit). Caller currently ignores the return value.
 */
export function openInviteUserModal(user: AuthUser): Promise<{
  results: BatchInviteResult[];
} | null> {
  // Org admins can only invite into their own org; the per-row and
  // default org inputs are hidden and the request goes out without
  // any org hint (the backend pins it to the caller's org).
  const isOrgAdmin = user.role === 'org_admin';

  return new Promise((resolve) => {
    const dialog = document.createElement('sl-dialog');
    dialog.label = 'Invite users';
    dialog.setAttribute('style', '--width: 720px');
    dialog.innerHTML = `
      <datalist id="${ORG_DATALIST_ID}"></datalist>

      <div id="iu-compose">
        <p style="margin: 0 0 0.75rem; color: var(--sl-color-neutral-700); font-size: 0.875rem">
          Paste a comma- or newline-separated list of email addresses.
          Each invited user gets a strong random temporary password and
          must change it on first sign-in.${
            isOrgAdmin
              ? ' New users join your organization automatically.'
              : ''
          }
        </p>

        <div class="field-stack">
          <sl-textarea
            id="iu-emails"
            label="Emails"
            help-text="Comma, semicolon, or newline separated."
            rows="4"
            resize="vertical"
            autocomplete="off"></sl-textarea>

          <label style="font-size:0.875rem; font-weight:500; ${
            isOrgAdmin ? 'display:none' : ''
          }">
            Default organization
            <input
              type="text"
              id="iu-default-org"
              list="${ORG_DATALIST_ID}"
              autocomplete="off"
              placeholder="Used for rows you leave empty"
              style="width:100%; padding:0.4rem 0.5rem; margin-top:0.25rem;
                     border:1px solid var(--sl-color-neutral-300);
                     border-radius: var(--sl-border-radius-medium); font: inherit" />
          </label>

          <div id="iu-rows-wrap" style="display:none">
            <div style="font-size:0.875rem; font-weight:500; margin-bottom:0.25rem">
              <span id="iu-rows-count">0</span> recipient(s)
            </div>
            <div id="iu-rows" class="iu-rows"></div>
          </div>

          <sl-alert id="iu-error" variant="danger" closable></sl-alert>
        </div>
      </div>

      <div id="iu-results" style="display:none">
        <div id="iu-summary" style="margin-bottom:0.5rem; font-size:0.875rem"></div>
        <div style="display:flex; gap:0.5rem; margin-bottom:0.5rem">
          <sl-button id="iu-copy-all" class="copy-feedback" size="small" variant="default">
            <sl-icon slot="prefix" name="clipboard"></sl-icon>
            <span class="iu-copy-all__label">Copy all (TSV)</span>
          </sl-button>
        </div>
        <div id="iu-results-table" class="iu-rows"></div>
      </div>

      <sl-button slot="footer" id="iu-cancel" variant="default">Cancel</sl-button>
      <sl-button slot="footer" id="iu-send" variant="primary">Send invites</sl-button>
      <sl-button slot="footer" id="iu-done" variant="primary" style="display:none">Done</sl-button>
    `;

    document.body.appendChild(dialog);

    const composeView = dialog.querySelector<HTMLElement>('#iu-compose')!;
    const resultsView = dialog.querySelector<HTMLElement>('#iu-results')!;
    const emailsEl = dialog.querySelector<HTMLTextAreaElement>('#iu-emails')!;
    const defaultOrgEl = dialog.querySelector<HTMLInputElement>('#iu-default-org')!;
    const rowsWrap = dialog.querySelector<HTMLElement>('#iu-rows-wrap')!;
    const rowsCountEl = dialog.querySelector<HTMLElement>('#iu-rows-count')!;
    const rowsEl = dialog.querySelector<HTMLElement>('#iu-rows')!;
    const errorEl = dialog.querySelector<HTMLElement & { open: boolean }>('#iu-error')!;
    const datalist = dialog.querySelector<HTMLDataListElement>(
      `#${ORG_DATALIST_ID}`,
    )!;

    const summaryEl = dialog.querySelector<HTMLElement>('#iu-summary')!;
    const copyAllBtn = dialog.querySelector<HTMLElement>('#iu-copy-all')!;
    const resultsTableEl = dialog.querySelector<HTMLElement>('#iu-results-table')!;

    const cancelBtn = dialog.querySelector<HTMLElement>('#iu-cancel')!;
    const sendBtn = dialog.querySelector<HTMLElement>('#iu-send')!;
    const doneBtn = dialog.querySelector<HTMLElement>('#iu-done')!;

    const showError = (msg: string) => {
      errorEl.textContent = msg;
      errorEl.open = true;
    };
    const hideError = () => {
      errorEl.open = false;
    };

    let parsedRows: ParsedRow[] = [];
    // Keyed by email; survives re-parses so the user doesn't lose org
    // input when they edit the email textarea.
    const rowOrgValues = new Map<string, string>();
    let lastResults: { results: BatchInviteResult[] } | null = null;

    // Lazy-load known orgs into the datalist; failure is non-fatal,
    // the user can still type any name (backend will create-on-the-fly).
    // Skipped for org admins — they don't see org inputs at all and
    // the /api/organizations endpoint is restricted to superadmins.
    if (!isOrgAdmin) {
      void api
        .listOrganizations()
        .then((res) => {
          const orgs = res.items ?? [];
          datalist.innerHTML = orgs
            .map((o: Organization) => `<option value="${escapeAttr(o.name)}"></option>`)
            .join('');
        })
        .catch((err) => {
          console.error('[invite-users] failed to list organizations', err);
        });
    }

    function renderRows(): void {
      if (parsedRows.length === 0) {
        rowsWrap.style.display = 'none';
        rowsEl.innerHTML = '';
        return;
      }
      rowsWrap.style.display = '';
      rowsCountEl.textContent = String(parsedRows.length);

      // Untouched rows (no entry in rowOrgValues) inherit the current
      // value of the default-org input. Once the user types into a row
      // its email is registered in rowOrgValues and the explicit value
      // wins (including a deliberately-cleared empty string — that
      // signals "leave it blank, do NOT reapply the default").
      // Org admins don't get the per-row org input at all — every
      // invitee lands in their organization.
      const defaultOrg = defaultOrgEl.value.trim();
      rowsEl.innerHTML = parsedRows
        .map((r, i) => {
          const explicit = rowOrgValues.get(r.email);
          const orgVal = explicit !== undefined ? explicit : defaultOrg;
          const validBadge = r.valid
            ? ''
            : `<sl-badge variant="danger" pill>invalid</sl-badge>`;
          const orgInput = isOrgAdmin
            ? ''
            : `<input
                type="text"
                class="iu-row__org"
                list="${ORG_DATALIST_ID}"
                autocomplete="off"
                placeholder="(use default)"
                data-email="${escapeAttr(r.email)}"
                value="${escapeAttr(orgVal)}" />`;
          return `
            <div class="iu-row" data-i="${i}">
              <div class="iu-row__email" title="${escapeAttr(r.email)}">
                ${escapeHtml(r.email)} ${validBadge}
              </div>
              ${orgInput}
            </div>`;
        })
        .join('');
    }

    function reparseEmails(): void {
      parsedRows = parseEmails(emailsEl.value);
      // Drop org-value memory for emails no longer in the list.
      const present = new Set(parsedRows.map((r) => r.email));
      for (const k of [...rowOrgValues.keys()]) {
        if (!present.has(k)) rowOrgValues.delete(k);
      }
      renderRows();
    }

    emailsEl.addEventListener('sl-input', () => {
      reparseEmails();
      hideError();
    });

    // Re-render existing rows when the default-org changes so any rows
    // the user hasn't explicitly touched visually pick up the new
    // default. Only re-renders the rows table (rowsEl); focus on the
    // default-org input itself isn't affected.
    defaultOrgEl.addEventListener('input', () => {
      renderRows();
    });

    rowsEl.addEventListener('input', (ev) => {
      const target = ev.target as HTMLInputElement | null;
      if (!target?.classList.contains('iu-row__org')) return;
      const email = target.dataset.email ?? '';
      rowOrgValues.set(email, target.value);
    });

    cancelBtn.addEventListener('click', () => dialog.hide());
    doneBtn.addEventListener('click', () => dialog.hide());

    dialog.addEventListener('sl-after-hide', () => {
      dialog.remove();
      resolve(lastResults);
    });

    sendBtn.addEventListener('click', async () => {
      hideError();
      reparseEmails();

      const valid = parsedRows.filter((r) => r.valid);
      if (valid.length === 0) {
        showError('Add at least one valid email');
        return;
      }
      const invalid = parsedRows.filter((r) => !r.valid);
      if (invalid.length > 0) {
        showError(
          `Fix or remove ${invalid.length} invalid entr${
            invalid.length === 1 ? 'y' : 'ies'
          } first`,
        );
        return;
      }

      // Org admins: every row is implicitly pinned to their org server-
      // side. We send entries without any organization hint and let the
      // route handler fill it in. Superadmins keep the per-row + default
      // org behavior.
      let entries: BatchInviteEntry[];
      if (isOrgAdmin) {
        entries = valid.map((r) => ({ email: r.email }));
      } else {
        const defaultOrg = defaultOrgEl.value.trim();
        entries = valid.map((r) => {
          const orgRaw = (rowOrgValues.get(r.email) ?? '').trim() || defaultOrg;
          return { email: r.email, organizationName: orgRaw };
        });
        const missingOrg = entries.find((e) => !e.organizationName);
        if (missingOrg) {
          showError(
            `${missingOrg.email}: organization required (set a default or fill the row)`,
          );
          return;
        }
      }

      const loadingBtn = sendBtn as HTMLButtonElement & { loading: boolean };
      loadingBtn.loading = true;
      try {
        const res = await api.batchInviteUsers(entries);
        lastResults = res;
        renderResults(res.results);
      } catch (err) {
        const apiErr = err as ApiError;
        showError(apiErr?.message ?? 'Failed to send invites');
      } finally {
        loadingBtn.loading = false;
      }
    });

    function renderResults(results: BatchInviteResult[]): void {
      composeView.style.display = 'none';
      resultsView.style.display = '';
      sendBtn.style.display = 'none';
      cancelBtn.style.display = 'none';
      doneBtn.style.display = '';

      const counts = { created: 0, conflict: 0, other: 0 };
      for (const r of results) {
        if (r.status === 'created') counts.created++;
        else if (r.status === 'conflict') counts.conflict++;
        else counts.other++;
      }
      summaryEl.innerHTML = `
        <strong>${counts.created}</strong> created
        &middot; <strong>${counts.conflict}</strong> already existed
        &middot; <strong>${counts.other}</strong> failed
      `;

      resultsTableEl.innerHTML = results
        .map((r) => {
          const variant = statusVariant(r.status);
          const pwd = r.tempPassword
            ? `<code class="iu-pwd">${escapeHtml(r.tempPassword)}</code>
               <sl-icon-button class="iu-copy-pwd copy-feedback" name="clipboard" label="Copy password"
                 data-pwd="${escapeAttr(r.tempPassword)}"></sl-icon-button>`
            : `<span style="color:var(--sl-color-neutral-500)">—</span>`;
          const errLine = r.error
            ? `<div class="iu-row__error">${escapeHtml(r.error)}</div>`
            : '';
          return `
            <div class="iu-result-row">
              <div class="iu-result-row__main">
                <div>
                  <code>${escapeHtml(r.email)}</code>
                  &middot;
                  ${r.organizationName ? escapeHtml(r.organizationName) : ''}
                </div>
                <sl-badge variant="${variant}" pill>${r.status}</sl-badge>
              </div>
              <div class="iu-result-row__pwd">${pwd}</div>
              ${errLine}
            </div>`;
        })
        .join('');
    }

    resultsTableEl.addEventListener('click', (ev) => {
      const target = (ev.target as HTMLElement).closest('.iu-copy-pwd') as
        | HTMLElement
        | null;
      if (!target) return;
      const pwd = target.dataset.pwd ?? '';
      void copyTextWithFeedback(pwd, target, { copiedLabel: 'Copied password' });
    });

    copyAllBtn.addEventListener('click', () => {
      if (!lastResults) return;
      void copyTextWithFeedback(resultsToTsv(lastResults.results), copyAllBtn, {
        copiedText: 'Copied',
        textSelector: '.iu-copy-all__label',
      });
    });

    requestAnimationFrame(() => {
      (dialog as HTMLElement & { show: () => void }).show();
    });
  });
}
